// src/hooks/observable/useObservableBadgeValue.ts
import { useState, useEffect, useContext, useCallback } from 'react';
import { AppContext } from '../../context/app-context';
import { NavMenuItemType } from "../../views/nav-menu/NavMenuSubmenuListItem";
import { KeyValueObservable } from '../../util/observers/KeyValueObservable';

const getBadgeValue = (menuItem: NavMenuItemType, observableMap: KeyValueObservable<any> | undefined): number => {
  if (!menuItem || !observableMap) {
    return 0;
  }

  if (menuItem.submenus && menuItem.submenus.length > 0) {
    return menuItem.submenus.reduce((total, submenu) => total + getBadgeValue(submenu, observableMap), 0);
  } else if (menuItem.badgeObservable) {
    const rawValue = observableMap.get(menuItem.badgeObservable.key);
    if (typeof rawValue === 'number') {
      return rawValue;
    }
    return parseInt(rawValue ?? '0', 10) || 0;
  }

  return 0;
};

const getObservableKeys = (menuItem: NavMenuItemType): string[] => {
  if (!menuItem) {
    return [];
  }

  if (menuItem.submenus && menuItem.submenus.length > 0) {
    return menuItem.submenus.flatMap(submenu => getObservableKeys(submenu));
  } else if (menuItem.badgeObservable) {
    return [menuItem.badgeObservable.key];
  }

  return [];
};

export function useObservableBadgeValue(menuItem: NavMenuItemType): number {
  const { observableMap } = useContext(AppContext);
  const [badgeValue, setBadgeValue] = useState(() => getBadgeValue(menuItem, observableMap));

  const recalculate = useCallback(() => {
    setBadgeValue(getBadgeValue(menuItem, observableMap));
  }, [menuItem, observableMap]);


  useEffect(() => {
    if (!observableMap) return;

    const observableKeys = getObservableKeys(menuItem);

    // Subscribe to observable changes
    observableKeys.forEach(key => {
      observableMap.subscribe?.(key, recalculate);
    });

    // Recalculate on mount
    recalculate();

    return () => {
      observableKeys.forEach(key => {
        observableMap.unsubscribe?.(key, recalculate);
      });
    };
  }, [observableMap, recalculate, menuItem]);

  return badgeValue;
}